/**
 * DocuSeal draws each settings menu inside the sidebar row. The row lists scroll
 * (`overflow-y: auto`), and that clips an absolute menu even when the row's
 * z-index is raised. A manual popover paints in the top layer, outside that
 * clip, while the lists keep scrolling.
 */

const GAP = 6;
const VIEWPORT_MARGIN = 8;

export interface Rect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export interface MenuPlacement {
  top: number;
  left: number;
  /** Scroll the menu when its content is taller than the space on the chosen side. */
  maxHeight: number;
}

/**
 * Pin a menu to its trigger. `end` is DocuSeal's dropdown-end (right edges
 * meet); `start` lines the left edges up. A side that cannot
 * show the whole menu yields to the roomier side, and a menu that fits neither
 * side is pinned to the viewport margin and scrolls.
 */
export function anchoredMenuPosition(
  trigger: Pick<Rect, "top" | "right" | "bottom" | "left">,
  menu: Pick<Rect, "width" | "height">,
  viewport: Pick<Rect, "width" | "height">,
  align: "start" | "end" = "end",
  gap = GAP
): MenuPlacement {
  const width = menu.width;
  const height = menu.height;
  const spaceBelow = Math.max(0, viewport.height - VIEWPORT_MARGIN - (trigger.bottom + gap));
  const spaceAbove = Math.max(0, trigger.top - gap - VIEWPORT_MARGIN);
  const fitsBelow = height <= spaceBelow;
  const fitsAbove = height <= spaceAbove;
  const placeBelow = fitsBelow || (!fitsAbove && spaceBelow >= spaceAbove);
  const maxHeight = placeBelow ? spaceBelow : spaceAbove;
  const top = placeBelow
    ? trigger.bottom + gap
    : fitsAbove
      ? trigger.top - gap - height
      : VIEWPORT_MARGIN;

  const aligned = align === "end" ? trigger.right - width : trigger.left;
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  const left = Math.min(Math.max(aligned, VIEWPORT_MARGIN), maxLeft);
  return { top, left, maxHeight };
}

const PLACED_PROPS = [
  "position",
  "top",
  "left",
  "right",
  "bottom",
  "margin",
  "visibility",
  "opacity",
  "max-height",
  "overflow-y",
] as const;

function menuOf(dropdown: Element): HTMLElement | null {
  const menu = dropdown.querySelector(":scope > .dropdown-content");
  return menu instanceof HTMLElement ? menu : null;
}

const popped = new WeakSet<HTMLElement>();

function popoverOpen(menu: HTMLElement) {
  try {
    return menu.matches(":popover-open");
  } catch {
    return popped.has(menu);
  }
}

function showMenu(menu: HTMLElement) {
  if (typeof menu.showPopover !== "function") return false;
  if (!menu.hasAttribute("popover")) menu.setAttribute("popover", "manual");
  if (popoverOpen(menu)) return true;
  try {
    menu.showPopover();
  } catch {
    return false;
  }
  popped.add(menu);
  return true;
}

function menuAlign(dropdown: Element): "start" | "end" {
  return dropdown.classList.contains("dropdown-end") ? "end" : "start";
}

/** Settings gears only. Type menus and Values rows stay in place so a drag can start. */
function canPromote(dropdown: Element) {
  return (
    !dropdown.classList.contains("field-types-dropdown") &&
    !dropdown.closest(".default-field") &&
    (dropdown.classList.contains("field-settings-dropdown") ||
      dropdown.classList.contains("field-area-settings-dropdown"))
  );
}

function placeMenu(menu: HTMLElement) {
  const dropdown = menu.closest(".dropdown");
  const trigger = dropdown?.querySelector(":scope > label") ?? dropdown;
  if (!dropdown || !trigger || !showMenu(menu)) return false;

  const bounds = menu.getBoundingClientRect();
  const { top, left, maxHeight } = anchoredMenuPosition(
    trigger.getBoundingClientRect(),
    { width: bounds.width, height: menu.scrollHeight },
    { width: window.innerWidth, height: window.innerHeight },
    menuAlign(dropdown)
  );
  menu.style.setProperty("position", "fixed", "important");
  menu.style.setProperty("top", `${top}px`, "important");
  menu.style.setProperty("left", `${left}px`, "important");
  menu.style.setProperty("right", "auto", "important");
  menu.style.setProperty("bottom", "auto", "important");
  menu.style.setProperty("margin", "0", "important");
  menu.style.setProperty("visibility", "visible", "important");
  menu.style.setProperty("opacity", "1", "important");
  menu.style.setProperty("max-height", `${maxHeight}px`, "important");
  menu.style.setProperty("overflow-y", "auto", "important");
  return true;
}

function releaseMenu(menu: HTMLElement) {
  popped.delete(menu);
  if (typeof menu.hidePopover === "function") {
    try {
      menu.hidePopover();
    } catch {
      // Already hidden, or the node was detached mid-close.
    }
  }
  PLACED_PROPS.forEach((prop) => menu.style.removeProperty(prop));
}

/**
 * Promote an open settings menu into the top layer and pin it to its trigger.
 * Type menus and Values rows are left alone: moving one with showPopover pulls
 * it out of the draggable row and Chrome never fires dragstart. A press waits
 * until the pointer lifts. After dragstart, Chrome sends pointercancel and no
 * pointerup, so that hold lasts until dragend.
 */
export function bindEscapedBuilderMenus(shadow: ShadowRoot) {
  const open = new Set<HTMLElement>();
  let frame = 0;
  let gesture = false;
  let dragging = false;

  const sync = () => {
    const stillOpen = new Set<HTMLElement>();
    shadow
      .querySelectorAll(".field-settings-dropdown, .field-area-settings-dropdown")
      .forEach((dropdown) => {
        if (!canPromote(dropdown)) return;
        const menu = menuOf(dropdown);
        if (!menu) return;
        const shown =
          dropdown.matches(":focus-within") || dropdown.classList.contains("dropdown-open");
        if (!shown || !placeMenu(menu)) return;
        stillOpen.add(menu);
        open.add(menu);
      });
    open.forEach((menu) => {
      if (stillOpen.has(menu)) return;
      releaseMenu(menu);
      open.delete(menu);
    });
  };

  const schedule = () => {
    if (gesture || frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      sync();
    });
  };

  const hold = () => {
    gesture = true;
    if (!frame) return;
    window.cancelAnimationFrame(frame);
    frame = 0;
  };

  const release = () => {
    if (!gesture) return;
    gesture = false;
    schedule();
  };

  const beginDrag = () => {
    dragging = true;
    hold();
  };

  // pointercancel/pointerup arrive mid-drag and must not promote until dragend.
  const releasePointer = () => {
    if (dragging) return;
    release();
  };

  const endDrag = () => {
    dragging = false;
    release();
  };

  shadow.addEventListener("focusin", schedule);
  shadow.addEventListener("focusout", schedule);
  shadow.addEventListener("scroll", schedule, true);
  shadow.addEventListener("pointerdown", hold, true);
  shadow.addEventListener("dragstart", beginDrag, true);
  // The builder portal follows the page on scroll and changes size in fullscreen
  // without a window resize, so both of those have to move an open menu.
  window.addEventListener("scroll", schedule, true);
  window.addEventListener("resize", schedule);
  window.addEventListener("pointerup", releasePointer, true);
  window.addEventListener("pointercancel", releasePointer, true);
  window.addEventListener("dragend", endDrag, true);
  const hostResize = new ResizeObserver(schedule);
  hostResize.observe(shadow.host);
  // The menu node appears on hover, which can be the same turn as the focus.
  const observer = new MutationObserver(schedule);
  observer.observe(shadow, { childList: true, subtree: true });

  return () => {
    window.cancelAnimationFrame(frame);
    observer.disconnect();
    hostResize.disconnect();
    shadow.removeEventListener("focusin", schedule);
    shadow.removeEventListener("focusout", schedule);
    shadow.removeEventListener("scroll", schedule, true);
    shadow.removeEventListener("pointerdown", hold, true);
    shadow.removeEventListener("dragstart", beginDrag, true);
    window.removeEventListener("scroll", schedule, true);
    window.removeEventListener("resize", schedule);
    window.removeEventListener("pointerup", releasePointer, true);
    window.removeEventListener("pointercancel", releasePointer, true);
    window.removeEventListener("dragend", endDrag, true);
    open.forEach(releaseMenu);
    open.clear();
  };
}
